import type { AiChatConversationContext } from '../aiChatContextTypes';
import type { AiChatPrompt } from './aiChatPromptTypes';

const formatCurrency = (value: unknown): string | null => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

const buildHoldingLabel = (symbol: string | null, name: string | null): string | null => {
  if (symbol && name) {
    return `${symbol} (${name})`;
  }

  return symbol ?? name;
};

const buildContextSummary = (context: AiChatConversationContext): string => {
  const { label, data } = context;
  const name = typeof data.name === 'string' ? data.name : null;
  const symbol = typeof data.symbol === 'string' ? data.symbol : null;
  const savedSoFar = formatCurrency(data.savedSoFar);
  const targetAmount = formatCurrency(data.targetAmount);
  const marketValue = formatCurrency(data.marketValue);

  if (name && savedSoFar && targetAmount) {
    return `${label} — ${name}: saved ${savedSoFar} of ${targetAmount} target.`;
  }

  const holdingLabel = buildHoldingLabel(symbol, name);
  if (holdingLabel && marketValue) {
    return `${label} — ${holdingLabel}: ${marketValue} market value.`;
  }

  if (holdingLabel) {
    return `${label} — ${holdingLabel}.`;
  }

  if (name) {
    return `${label} — ${name}.`;
  }

  return label;
};

/**
 * Short, human-friendly text shown in the user's chat bubble and persisted as the user message.
 */
export const buildChipUserMessage = (prompt: AiChatPrompt, context?: AiChatConversationContext): string =>
  buildPromptDisplayText(prompt, context);

/**
 * Model-only instructions posted as hidden SYSTEM context before the user message.
 * Returns null when the visible user message already carries the full prompt.
 */
export const buildChipHiddenContext = (prompt: AiChatPrompt, context?: AiChatConversationContext): string | null => {
  if (context) {
    const summary = buildContextSummary(context);
    const focusLine = summary.endsWith('.') ? summary : `${summary}.`;
    return `${prompt.actualPrompt}\n\nFocus: ${focusLine}`;
  }

  const userText = buildPromptDisplayText(prompt);
  if (prompt.actualPrompt !== userText) {
    return prompt.actualPrompt;
  }

  return null;
};

const getFocusToken = (context: AiChatConversationContext): string | null => {
  const symbol = typeof context.data.symbol === 'string' ? context.data.symbol : null;
  const name = typeof context.data.name === 'string' ? context.data.name : null;
  return symbol ?? name;
};

/**
 * Short, human-friendly text shown in the user's chat bubble.
 *
 * Keeps the chip's concise display label and appends only the focus token
 * (e.g. a holding symbol or goal name) so the user sees what they asked about —
 * without the detailed model instructions or tool plumbing.
 */
export const buildPromptDisplayText = (prompt: AiChatPrompt, context?: AiChatConversationContext): string => {
  const focusToken = context ? getFocusToken(context) : null;
  return focusToken ? `${prompt.displayPrompt} (${focusToken})` : prompt.displayPrompt;
};
